import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export interface MonthRecord {
  month: string;
  employeePF: string;
  employerPF: string;
  voluntaryPF: string;
  pensionContr: string;
}

export interface PfTotals {
  employeePF: string;
  employerPF: string;
  voluntaryPF: string;
  pensionContr: string;
}

export interface PfFetchResult {
  months: MonthRecord[];
  totals: PfTotals;
}

function unwrapList(res: any): any[] {
  const root = res?.data ?? res;
  const raw = root?.data ?? root;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.list)) return raw.list;
  if (Array.isArray(raw?.records)) return raw.records;
  if (Array.isArray(root)) return root;
  if (Array.isArray(res)) return res;
  return [];
}

function toError(err: any, fallback: string): Error {
  const msg =
    err?.response?.data?.data?.message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;
  return new Error(typeof msg === "string" ? msg : fallback);
}

function pick(record: any, keys: string[], fallback = ""): string {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return fallback;
}

export function formatAmount(value: any): string {
  const num = parseFloat(String(value ?? "0").replace(/,/g, ""));
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
}

function toMonthRecord(record: any): MonthRecord {
  return {
    month: pick(record, ["month", "Month", "MONTH"], "-"),
    employeePF: formatAmount(
      pick(record, ["employeePFContribution", "EmployeePFContribution", "empPF"])
    ),
    employerPF: formatAmount(
      pick(record, ["employerPFContribution", "EmployerPFContribution", "emprPF"])
    ),
    voluntaryPF: formatAmount(
      pick(record, ["voluntaryPFContribution", "VoluntaryPFContribution", "volPF"])
    ),
    pensionContr: formatAmount(
      pick(record, [
        "employerPensionContribution",
        "EmployerPensionContribution",
        "pensionContribution",
      ])
    ),
  };
}

export async function fetchPfDetails(year: string): Promise<PfFetchResult> {
  try {
    const res: any = await Api.post(API_URLS.ANNUAL_NPS_PF.PF_GET_DETAILS, {
      year,
      type: "MONTHLY",
    });

    const root = res?.data ?? res;
    if (res?.success === false || root?.success === false) {
      throw new Error(res?.message || root?.message || "Failed to fetch PF details");
    }

    const records = unwrapList(res);
    const months = records.map(toMonthRecord);

    const sum = (field: keyof MonthRecord) =>
      months.reduce((acc, row) => acc + parseFloat(row[field] as string), 0).toFixed(2);

    return {
      months,
      totals: {
        employeePF: sum("employeePF"),
        employerPF: sum("employerPF"),
        voluntaryPF: sum("voluntaryPF"),
        pensionContr: sum("pensionContr"),
      },
    };
  } catch (err: any) {
    throw toError(err, "Something went wrong");
  }
}
